/** @jsxRuntime classic */
/** @jsx jsx */

import { css, jsx, keyframes } from '@emotion/react';
import { Colors, Typography } from '@zapier/design-system';
import { labelStyles } from '@zapier/style-encapsulation';
import { ReactNode } from 'react';
import { MEDIA_MIN_WIDTH } from '../../../constants';
import { useAvroTracking } from '../../../observability';
import { RichTextHtml } from '../../RichTextHtml/RichTextHtml';
import { RichTextHTMLString } from '../../sections/types';
import { CustomBrandButton } from '../../CustomBrandButton';

export type HeroCenteredProps = {
  heading: string;
  description?: RichTextHTMLString;
  buttons?: {
    text?: string;
    url?: string;
    variant?: 'primary' | 'primary-alt' | 'secondary';
    showGoogleIcon?: boolean;
  }[];
  checklistItems?: { text: RichTextHTMLString }[];
  backgroundColor?:
    | 'neutral100'
    | 'lavender'
    | 'peach'
    | 'bolt'
    | 'sky'
    | 'cream';
  size?: 'regular' | 'large';
  children?: ReactNode;
};

const fadeIn = keyframes({
  from: {
    opacity: 0,
    transform: 'translateY(5vh)',
  },
  to: {
    from: {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
});

const Styles = labelStyles('HeroCentered', {
  root: (
    backgroundColor: HeroCenteredProps['backgroundColor'] = 'neutral100',
  ) =>
    css({
      padding: '32px 20px 16px',
      backgroundColor: Colors[backgroundColor],

      '&[data-size="large"]': {
        paddingTop: '48px',
      },

      [MEDIA_MIN_WIDTH.small]: {
        padding: '48px 20px 24px',
      },

      [MEDIA_MIN_WIDTH.large]: {
        padding: '96px 20px 48px',

        '&[data-size="large"]': {
          paddingTop: '104px',
        },
      },
    }),

  content: css({
    maxWidth: '1170px',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  }),

  heading: css({
    fontFamily: Typography.heading.fontFamily,
    fontSize: '40px',
    fontWeight: 600,
    textAlign: 'center',
    lineHeight: '42px',
    letterSpacing: '1px',
    color: Colors.earth,
    maxWidth: '512px',

    '[data-size="large"] &': {
      fontSize: '46px',
    },

    [MEDIA_MIN_WIDTH.small]: {
      fontSize: '60px',
      lineHeight: 0.9,
      maxWidth: '528px',

      '[data-size="large"] &': {
        fontSize: '64px',
        maxWidth: '576px',
      },
    },

    [MEDIA_MIN_WIDTH.large]: {
      fontSize: '90px',
      lineHeight: 0.8,
      maxWidth: '784px',

      '[data-size="large"] &': {
        fontSize: '110px',
        maxWidth: '896px',
      },
    },
  }),

  description: css({
    fontSize: '18px',
    fontWeight: 500,
    letterSpacing: '-0.5px',
    textAlign: 'center',
    color: Colors.earth,
    maxWidth: '384px',
    marginTop: '16px',

    [MEDIA_MIN_WIDTH.small]: {
      marginTop: '20px',
    },

    [MEDIA_MIN_WIDTH.large]: {
      fontSize: '21px',
      letterSpacing: '-1px',
      maxWidth: '832px',
      marginTop: '44px',
    },
  }),

  buttons: css({
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    marginTop: '20px',

    'h1 + &': {
      marginTop: '32px',
    },

    [MEDIA_MIN_WIDTH.small]: {
      flexDirection: 'row',
      marginTop: '40px',

      'h1 + &': {
        marginTop: '48px',
      },
    },

    [MEDIA_MIN_WIDTH.large]: {
      marginTop: '34px',

      'h1 + &': {
        marginTop: '40px',
      },
    },
  }),

  checklist: css({
    display: 'flex',
    flexDirection: 'column',
    flexWrap: 'wrap',
    justifyContent: 'center',
    columnGap: '32px',
    rowGap: '8px',
    listStyleImage: 'url("/l/images/checkmark.svg")',
    color: Colors.neutral800,
    marginTop: '40px',
    maxWidth: '832px',
    fontSize: '12px',

    [MEDIA_MIN_WIDTH.small]: {
      flexDirection: 'row',
      marginTop: '21px',
      fontSize: '14px',
    },

    [MEDIA_MIN_WIDTH.large]: {
      marginTop: '47px',
      fontSize: '15px',
    },
  }),

  checklistItem: css({
    paddingLeft: '4px',
  }),

  media: css({
    width: '100vw',
    marginTop: '32px',

    '@media (prefers-reduced-motion: no-preference)': {
      animation: `${fadeIn} 800ms cubic-bezier(0.65, 0, 0.35, 1) both`,
    },

    'ul + &': {
      marginTop: '16px',
    },

    [MEDIA_MIN_WIDTH.small]: {
      width: '100%',
      marginTop: '48px',

      'ul + &': {
        marginTop: '26px',
      },
    },
  }),
});

export function HeroCentered(props: HeroCenteredProps) {
  const { emitCTAClickEvent } = useAvroTracking();

  return (
    <div
      css={Styles.root(props.backgroundColor)}
      data-size={props.size ?? 'regular'}
    >
      <div css={Styles.content}>
        <h1 css={Styles.heading}>{props.heading}</h1>
        {props.description && (
          <div css={Styles.description}>
            <RichTextHtml
              html={props.description}
              backgroundColor={props.backgroundColor}
            />
          </div>
        )}
        {props.buttons && props.buttons.length > 0 && (
          <div css={Styles.buttons}>
            {props.buttons.map((btn, index) => (
              <CustomBrandButton
                key={index}
                variant={btn.variant}
                href={btn.url}
                showGoogleIcon={btn.showGoogleIcon}
                onClick={() => {
                  emitCTAClickEvent({
                    text: btn.text ?? '',
                    href: btn.url ?? '',
                    section: 'HeroCentered',
                  });
                }}
              >
                {btn.text}
              </CustomBrandButton>
            ))}
          </div>
        )}
        {props.checklistItems && props.checklistItems.length > 0 && (
          <ul css={Styles.checklist}>
            {props.checklistItems.map((item, index) => (
              <li css={Styles.checklistItem} key={index}>
                <RichTextHtml
                  html={item.text}
                  backgroundColor={props.backgroundColor}
                />
              </li>
            ))}
          </ul>
        )}
        <div css={Styles.media}>{props.children}</div>
      </div>
    </div>
  );
}
